// Archivo: ProduccionView.js (CORREGIDO PARA LOTES GRANDES > 30)

import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, doc, query, orderBy, runTransaction, getDocs, where, serverTimestamp } from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import Modal from '../../ui/Modal';
import ProductionOrderForm from '../../ui/ProductionOrderForm';
import BatchDetailView from '../../ui/BatchDetailView';

const PrintIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2-4h6m-6-4v2m6-4v2" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 hover:text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const EyeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>;

// --- INICIO DE NUEVA FUNCIÓN AUXILIAR ---
// Función para realizar consultas 'in' en trozos de 30 para evitar el límite de Firestore.
const getDocsInChunks = async (collectionRef, field, values) => {
    if (!values.length) return [];
    const chunks = [];
    for (let i = 0; i < values.length; i += 30) {
        chunks.push(values.slice(i, i + 30));
    }
    const results = [];
    for (const chunk of chunks) {
        const q = query(collectionRef, where(field, 'in', chunk));
        const snapshot = await getDocs(q);
        snapshot.forEach(doc => results.push(doc));
    }
    return results;
};
// --- FIN DE NUEVA FUNCIÓN AUXILIAR ---

export const ProduccionView = ({ db, showModal, currentUser, catalogs, stock, permissions }) => {
    const [productionOrders, setProductionOrders] = useState([]);
    const [batchItems, setBatchItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedBatchForDetail, setSelectedBatchForDetail] = useState(null);
    const [editingItem, setEditingItem] = useState(null);

    const stockInProduction = useMemo(() => {
        if (!stock || !catalogs) return [];
        const productionLocation = catalogs.locations.find(l => l.name.toLowerCase() === 'producción');
        if (!productionLocation) return [];
        return stock.filter(item => 
            item.locationId === productionLocation.id && 
            (!item.designId || item.designId === 'NONE')
        );
    }, [stock, catalogs]);

    const groupedStockForForm = useMemo(() => {
        const grouped = stockInProduction.reduce((acc, item) => {
            const key = `${item.productId}_${item.size}_${item.color}`;
            if (!acc[key]) {
                acc[key] = {
                    conceptualId: key,
                    conceptualName: `${item.productName} - Talla: ${item.size}, Color: ${item.color}`,
                    productId: item.productId,
                    productName: item.productName,
                    size: item.size,
                    color: item.color,
                    totalQuantity: 0,
                    sources: []
                };
            }
            acc[key].totalQuantity += item.quantity;
            acc[key].sources.push({ id: item.id, quantity: item.quantity });
            return acc;
        }, {});
        return Object.values(grouped);
    }, [stockInProduction]);

    const availableStockForForm = useMemo(() => {
        if (!groupedStockForForm) return [];
        const subtractions = batchItems.reduce((acc, item) => {
            if (editingItem && item.tempId === editingItem.tempId) return acc;
            const key = `${item.sourceProductGroup.productId}_${item.sourceProductGroup.size}_${item.sourceProductGroup.color}`;
            acc[key] = (acc[key] || 0) + item.quantity;
            return acc;
        }, {});

        return groupedStockForForm
            .map(item => {
                if (subtractions[item.conceptualId]) {
                    return { ...item, totalQuantity: item.totalQuantity - subtractions[item.conceptualId] };
                }
                return item;
            })
            .filter(item => item.totalQuantity > 0);
    }, [groupedStockForForm, batchItems, editingItem]);

    const batchTotalQuantity = useMemo(() => {
        return batchItems.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);
    }, [batchItems]);

    useEffect(() => {
        setIsLoading(false);
    }, [catalogs]);

    useEffect(() => {
        const q = query(collection(db, 'inventory_production_orders'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setProductionOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, () => { 
            showModal("Error", "No se pudieron cargar las órdenes de producción.");
        });
        return () => unsubscribe();
    }, [db, showModal]);

    const groupedOrders = useMemo(() => {
        if (productionOrders.length === 0 || !catalogs) return [];
        const groups = productionOrders.reduce((acc, order) => {
            const batchId = order.batchId || order.id;
            if (!acc[batchId]) {
                acc[batchId] = {
                    batchId, createdBy: order.createdBy,
                    createdAtFormatted: order.createdAt?.toDate().toLocaleString('es-GT', { timeZone: 'America/Guatemala' }),
                    items: [], totalQuantity: 0,
                };
            }
            const product = catalogs.products.find(p => p.id === order.productId);
            const design = catalogs.designs.find(d => d.id === order.designId);
            acc[batchId].items.push({
                ...order,
                productName: product?.name || 'N/A',
                designName: design ? `${design.name} (${order.designSize || ''})` : 'Sin Diseño',
            });
            acc[batchId].totalQuantity += order.quantity;
            return acc;
        }, {});
        return Object.values(groups);
    }, [productionOrders, catalogs]);
    
    const handleAddToBatch = (formData) => {
        const design = catalogs.designs.find(d => d.id === formData.designId);
        setBatchItems(prev => [...prev, {
            ...formData, tempId: Date.now(),
            productName: formData.sourceProductGroup.productName,
            designName: design.name, 
            size: formData.sourceProductGroup.size,
            color: formData.sourceProductGroup.color,
        }]);
        setIsModalOpen(false);
    };

    const handleUpdateBatchItem = (formData) => {
        const design = catalogs.designs.find(d => d.id === formData.designId);
        const updatedItem = {
            ...formData,
            tempId: editingItem.tempId,
            productName: formData.sourceProductGroup.productName,
            designName: design.name,
            size: formData.sourceProductGroup.size,
            color: formData.sourceProductGroup.color,
        };
        setBatchItems(prevItems => prevItems.map(item => item.tempId === editingItem.tempId ? updatedItem : item));
        setEditingItem(null);
        setIsModalOpen(false);
    };

    const handleStartEdit = (itemToEdit) => {
        setEditingItem(itemToEdit);
        setIsModalOpen(true);
    };

    const handleRemoveFromBatch = (tempId) => {
        setBatchItems(prev => prev.filter(item => item.tempId !== tempId));
    };

    const handleCommitProductionBatch = async () => {
        if (batchItems.length === 0) return showModal("Lote Vacío", "No hay órdenes en el lote para procesar.");
        setIsLoading(true);
        const batchId = `prod-${Date.now()}`;
        try {
            const aggregatedItems = batchItems.reduce((acc, item) => {
                const { sourceProductGroup, quantity, designId, designSize, destinationLocationId } = item;
                const finalDesignIdForSku = designId || 'NONE';
                const finalDesignSizeForSku = (designSize || sourceProductGroup.size || '').trim() || 'NONE';
                const newProductSku = `${sourceProductGroup.productId}_${finalDesignIdForSku}_${sourceProductGroup.size}_${sourceProductGroup.color}_${finalDesignSizeForSku}_${destinationLocationId}`;
                
                if (!acc[newProductSku]) {
                    acc[newProductSku] = {
                        itemDetails: item,
                        totalQuantity: 0,
                        sku: newProductSku,
                        finalDesignSizeForDb: finalDesignSizeForSku === 'NONE' ? null : finalDesignSizeForSku,
                    };
                }
                acc[newProductSku].totalQuantity += quantity;
                return acc;
            }, {});
            const finalBatch = Object.values(aggregatedItems);

            const allSourceRefs = new Map();
            const allDestSKUs = finalBatch.map(item => item.sku);

            batchItems.forEach(item => {
                item.sourceProductGroup.sources.forEach(source => {
                    if (!allSourceRefs.has(source.id)) {
                        allSourceRefs.set(source.id, doc(db, 'inventory_stock', source.id));
                    }
                });
            });
            
            // --- INICIO DE CORRECCIÓN ---
            // Se utiliza la nueva función para manejar lotes grandes.
            const destDocs = await getDocsInChunks(collection(db, 'inventory_stock'), 'sku', allDestSKUs);
            const destDocsMap = new Map(destDocs.map(d => [d.data().sku, d]));
            // --- FIN DE CORRECCIÓN ---

            await runTransaction(db, async (transaction) => {
                const sourceDocsPromises = Array.from(allSourceRefs.values()).map(ref => transaction.get(ref));
                const sourceDocs = await Promise.all(sourceDocsPromises);
                const sourceDocsMap = new Map(sourceDocs.map(doc => [doc.id, doc]));
                const sourceDeductions = new Map();

                for (const item of batchItems) {
                    let remainingQtyToValidate = item.quantity;
                    for (const source of item.sourceProductGroup.sources) {
                        if (remainingQtyToValidate <= 0) break;
                        const sourceDoc = sourceDocsMap.get(source.id);
                        if (!sourceDoc || !sourceDoc.exists()) throw new Error(`El stock de origen ${source.id} no existe.`);
                        const alreadyReserved = sourceDeductions.get(source.id) || 0;
                        const availableInDoc = sourceDoc.data().quantity - alreadyReserved;
                        if (availableInDoc < 0) throw new Error("Error de cálculo de stock negativo.");
                        const qtyToReserveFromDoc = Math.min(remainingQtyToValidate, availableInDoc);
                        if (qtyToReserveFromDoc > 0) {
                           sourceDeductions.set(source.id, alreadyReserved + qtyToReserveFromDoc);
                           remainingQtyToValidate -= qtyToReserveFromDoc;
                        }
                    }
                    if (remainingQtyToValidate > 0) {
                        throw new Error(`Stock insuficiente para "${item.productName}", faltan ${remainingQtyToValidate} unidades.`);
                    }
                }

                for (const [docId, quantityToDeduct] of sourceDeductions.entries()) {
                    const docRef = allSourceRefs.get(docId);
                    const docSnap = sourceDocsMap.get(docId);
                    const newQuantity = docSnap.data().quantity - quantityToDeduct;
                    transaction.update(docRef, { quantity: newQuantity });
                }

                for (const aggregatedItem of finalBatch) {
                    const { itemDetails, totalQuantity, sku, finalDesignSizeForDb } = aggregatedItem;
                    const { sourceProductGroup, designId, destinationLocationId } = itemDetails;
                    const destDocSnap = destDocsMap.get(sku);

                    if (destDocSnap) {
                        const newDestQty = destDocSnap.data().quantity + totalQuantity;
                        transaction.update(destDocSnap.ref, { quantity: newDestQty });
                    } else {
                        const newStockRef = doc(collection(db, 'inventory_stock'));
                        transaction.set(newStockRef, {
                            productId: sourceProductGroup.productId, 
                            designId, 
                            designSize: finalDesignSizeForDb,
                            size: sourceProductGroup.size, 
                            color: sourceProductGroup.color,
                            quantity: totalQuantity, 
                            locationId: destinationLocationId, 
                            sku: sku,
                        });
                    }

                    const orderRef = doc(collection(db, 'inventory_production_orders'));
                     transaction.set(orderRef, {
                        productId: sourceProductGroup.productId, designId, designSize: finalDesignSizeForDb, size: sourceProductGroup.size, color: sourceProductGroup.color, quantity: totalQuantity, locationId: destinationLocationId, sku: sku,
                        batchId, createdBy: currentUser.name, creatorId: currentUser.uid, createdAt: serverTimestamp(), productName: sourceProductGroup.productName, designName: itemDetails.designName
                    });
                }
            });

            showModal("Éxito", "Lote de producción procesado correctamente.");
            setBatchItems([]);
        } catch (error) {
            console.error("Error detallado en la transacción de producción:", error);
            showModal("Error en Transacción", `Ocurrió un error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };
    
    const handlePrintProductionBatch = (batch) => {
        const doc = new jsPDF();
        const logoImg = document.getElementById('company-logo-for-pdf');
        if (logoImg) { doc.addImage(logoImg, 'PNG', 15, 12, 50, 15); }
        doc.setFontSize(18);
        doc.text(`Orden de Producción de Lote #${batch.batchId}`, 15, 40);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Registrado por: ${batch.createdBy || 'N/A'}`, 15, 47);
        doc.text(`Fecha de Registro: ${batch.createdAtFormatted || 'N/A'}`, 15, 52);
        const generatorName = currentUser?.name || 'Usuario Desconocido';
        const generationDate = new Date().toLocaleString('es-GT', { timeZone: 'America/Guatemala' });
        doc.text(`Generado por: ${generatorName} el ${generationDate}`, 15, 57);
        const tableColumns = ["Producto", "Diseño", "Talla Prod.", "Talla Diseño", "Color", "Cantidad"];
        const tableRows = batch.items.map(item => [
            item.productName || 'N/A', item.designName || 'N/A', 
            item.size || 'N/A', item.designSize || 'N/A',
            item.color || 'N/A', item.quantity || 0
        ]);
        autoTable(doc, {
            head: [tableColumns], body: tableRows, startY: 65, headStyles: { fillColor: [22, 160, 133] },
        });
        const finalY = doc.lastAutoTable.finalY;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Cantidad Total de Productos en el Lote: ${batch.totalQuantity}`, 15, finalY + 10);
        doc.save(`lote-produccion-${batch.batchId}.pdf`);
    };

    const handleViewDetails = (batch) => {
        const batchWithDetails = { ...batch, type: 'Producción' };
        setSelectedBatchForDetail(batchWithDetails);
        setIsDetailModalOpen(true);
    };

    const handleCancelModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md space-y-8">
            <img id="company-logo-for-pdf" src="/aralellum.png" style={{ display: 'none' }} alt="Logo"/>
            <div>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold text-gray-800">Crear Lote de Producción</h3>
                    {permissions?.INVENTORY?.manage_production && (
                        <button onClick={() => { setEditingItem(null); setIsModalOpen(true); }} className="btn-primary"> + Añadir Orden al Lote </button>
                    )}
                </div>
                <p className="text-gray-600">Añade una o más órdenes a un lote y luego procésalo para afectar el inventario.</p>
                {stockInProduction.length === 0 && !isLoading && (
                    <p className="text-sm text-orange-600 bg-orange-100 border border-orange-200 rounded-md p-3 mt-4">
                        <strong>Atención:</strong> No hay materia prima (productos lisos) en la ubicación de 'Producción'. 
                        Primero debes hacer un <strong className="font-semibold">traslado</strong> desde una bodega hacia 'Producción'.
                    </p>
                )}
            </div>

            {batchItems.length > 0 && (
                <div className="p-4 border rounded-lg bg-sky-50">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-semibold text-gray-700">Lote de Producción Pendiente ({batchItems.length} órdenes)</h4>
                        <button onClick={handleCommitProductionBatch} className="btn-success">Procesar Lote de Producción</button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                           <thead className="bg-gray-200">
                                <tr>
                                    <th className="p-2 text-left text-sm font-semibold text-gray-600">Producto Base</th>
                                    <th className="p-2 text-left text-sm font-semibold text-gray-600">Diseño Aplicado</th>
                                    <th className="p-2 text-right text-sm font-semibold text-gray-600">Cantidad</th>
                                    <th className="p-2 text-center text-sm font-semibold text-gray-600">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {batchItems.map((item) => (
                                    <tr key={item.tempId} className="border-b">
                                        <td className="p-2">{item.productName} ({item.size}, {item.color})</td>
                                        <td className="p-2">{item.designName}</td>
                                        <td className="p-2 text-right font-bold">{item.quantity}</td>
                                        <td className="p-2 text-center">
                                            <div className="flex justify-center items-center space-x-2">
                                                <button onClick={() => handleStartEdit(item)} className="text-sky-600 hover:text-sky-800 p-1" title="Editar orden">
                                                    <EditIcon />
                                                </button>
                                                <button onClick={() => handleRemoveFromBatch(item.tempId)} className="text-red-500 hover:text-red-700 p-1" title="Quitar del lote">
                                                    <TrashIcon />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t-2 border-gray-300">
                                    <td colSpan="2" className="p-2 text-right font-semibold text-gray-800">Total a Producir:</td>
                                    <td className="p-2 text-right font-bold text-lg text-gray-800">{batchTotalQuantity}</td>
                                    <td className="p-2"></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}

            <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Historial de Lotes de Producción</h3>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1000px]">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">Fecha</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">ID del Lote</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">Creado por</th>
                                <th className="p-3 text-right text-sm font-semibold text-gray-600">Cantidad Total</th>
                                <th className="p-3 text-center text-sm font-semibold text-gray-600">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (<tr><td colSpan="5" className="text-center p-4">Cargando...</td></tr>) : groupedOrders.map(batch => (
                                <tr key={batch.batchId} className="border-b">
                                    <td className="p-3">{batch.createdAtFormatted}</td>
                                    <td className="p-3 font-mono text-xs">{batch.batchId}</td>
                                    <td className="p-3">{batch.createdBy}</td>
                                    <td className="p-3 text-right font-bold">{batch.totalQuantity}</td>
                                    <td className="p-3 text-center">
                                        <div className="flex justify-center items-center gap-4">
                                            <button onClick={() => handleViewDetails(batch)} title="Ver Detalles del Lote">
                                                <EyeIcon />
                                            </button>
                                            <button onClick={() => handlePrintProductionBatch(batch)} title="Imprimir Lote">
                                                <PrintIcon />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                     {groupedOrders.length === 0 && !isLoading && <p className="text-center text-gray-500 py-8">No hay lotes de producción registrados.</p>}
                </div>
            </div>

            <Modal isOpen={isModalOpen} onClose={handleCancelModal} title={editingItem ? "Editar Orden de Producción" : "Añadir Orden al Lote de Producción"}>
                <ProductionOrderForm 
                    onSave={editingItem ? handleUpdateBatchItem : handleAddToBatch} 
                    onCancel={handleCancelModal}
                    initialData={editingItem}
                    stock={availableStockForForm}
                    {...{ 
                        showModal, 
                        designs: catalogs?.designs || [], 
                        locations: catalogs?.locations || [], 
                        currentUser 
                    }} 
                />
            </Modal>

            <Modal isOpen={isDetailModalOpen} onClose={() => setIsDetailModalOpen(false)} title="Detalle de Lote de Producción">
                <BatchDetailView batch={selectedBatchForDetail} currentUser={currentUser} />
            </Modal>
        </div>
    );
};